<template>
    <div class="chart">
        <div class="chart-content" ref="chartContent">
            <svg class="svg-content" ref="svgcontent"></svg>
        </div>

        <div class="tooltip chart-tooltip" ref="tooltip">
            {{ tooltipData.nome != null ? tooltipData.nome.toLocaleUpperCase() : '' }}
            <br />
            <table class="tooltip-table">
                <tbody>
                    <tr>
                        <td>{{ $t('percentual').toLocaleUpperCase() }}:</td>
                        <td>
                            {{ tooltipData.porc != undefined ? getDecimal(tooltipData.porc) : '' }}%
                        </td>
                    </tr>
                    <tr>
                        <td>{{ $t('vendas').toLocaleUpperCase() }}:</td>
                        <td>
                            {{
                                tooltipData.total != undefined
                                    ? getDecimal(tooltipData.total, null, true)
                                    : ''
                            }}
                        </td>
                    </tr>
                    <tr>
                        <td>{{ $t('total').toLocaleUpperCase() }}:</td>
                        <td>
                            {{ total != undefined ? getDecimal(total, null, true) : '' }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import { palette as paleta } from '@/functions/color/Palette';

export default {
    props: {
        data: {
            type: Array,
            default: [],
        },
        referencia: {
            type: String,
            default: '',
        },
        total: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            cores: [],
            tooltipData: {
                nome: null,
                porc: null,
                total: null,
            },
            formatter: new Intl.NumberFormat(),
        };
    },

    created() {
        this.cores = paleta.getBarColors();
    },
    mounted() {
        if (this.$refs.tooltip != undefined) {
            document.querySelector('.flex-content').append(this.$refs.tooltip);
        }

        this.drawChart();
    },
    beforeDestroy() {
        document.querySelector('.flex-content .tooltip').remove();
    },
    methods: {
        drawChart() {
            let margin = { top: 20, right: 20, bottom: 30, left: 25 }; // Margens
            let width = this.$refs.chartContent.offsetWidth; // Largura do container
            let height = this.$refs.chartContent.offsetHeight; // Altura do container
            let h = height - margin.top - margin.bottom; // Altura sem margens
            let w = width - margin.left - margin.right; // Largura sem margens
            let each = 100; // Largura de cada vendedor no gráfico
            let nw = this.data.length * each; // Largura necessária
            let uw = nw < w ? w : nw; // Largura que será usada: do container caso haja espaço, a necessária caso contrário

            let barPadding = 0.5;

            const svg = this.$d3.select(this.$refs.svgcontent);

            let tooltip = this.$d3.select(this.$refs.tooltip);

            svg.attr('width', uw == nw ? uw + 50 : width);

            const chart = svg
                .append('g')
                .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

            let x0 = this.$d3.scaleBand().padding(0.1);

            let x1 = this.$d3.scaleBand().padding(0.2);

            let y = this.$d3.scaleLinear().range([h, 0]);

            let color = this.$d3.scaleOrdinal(this.cores);

            let xAxis = this.$d3.axisBottom(x0);

            let yAxis = this.$d3.axisLeft(y).tickFormat(this.customFormatter);

            let rowNames = this.data.map(function (d) {
                return d.nome;
            });

            x0.domain(rowNames).range([0, uw]);
            x1.domain(['porc']).range([0, x0.bandwidth()]);

            // Domínio do eixo Y
            y.domain([
                0,
                this.$d3.max(this.data, (d) => {
                    return d.porc;
                }),
            ]);

            let textsize = 0.85 * each;

            let wrap = function (t) {
                let length = this.getComputedTextLength();
                let clipped = false;

                while (length > textsize && this.textContent.length > 0) {
                    this.textContent = this.textContent.slice(0, -1);
                    length = this.getComputedTextLength();
                    clipped = true;
                }

                if (clipped) {
                    this.textContent = this.textContent.trim() + '…';
                }
            };

            // Desenho do eixo X
            chart
                .append('g')
                .attr('class', 'x axis')
                .style('opacity', '0')
                .attr('transform', 'translate(0,' + h + ')')
                .call(xAxis)
                .selectAll('text')
                .each(wrap);

            // Desenho do eixo Y
            chart.append('g').attr('class', 'y axis').style('opacity', '0').call(yAxis);

            // Área onde ficarão as barras
            let slice = chart
                .selectAll('.slice')
                .data(this.data)
                .enter()
                .append('g')
                .attr('class', 'g')
                .attr('transform', function (d) {
                    return `translate(${x0(d.nome)},0)`;
                });

            let delay = 100;

            // Desenho das barras em si
            slice
                .selectAll('rect')
                .data(function (d, i) {
                    let r = [];

                    r.push({
                        nome: 'porc',
                        value: d.porc,
                        vendedor: d.nome,
                        index: i,
                        total: d.total,
                    });

                    return r;
                })
                .enter()
                .append('rect')
                .attr('class', 'bars')
                .attr('width', x1.bandwidth())
                .attr('x', function (d) {
                    return x1(d.nome);
                })
                .style('fill', (d) => color(d.nome))
                .style('opacity', '0')
                .attr('y', function (d) {
                    return y(d.value);
                })
                .attr('height', function (d) {
                    return h - y(d.value);
                })
                // Eventos de mouse
                .on('mouseover', (d) => {
                    this.tooltipData.nome = d.vendedor;
                    this.tooltipData.porc = d.value;
                    this.tooltipData.total = d.total;

                    tooltip.transition().duration(200).style('opacity', '1');

                    tooltip
                        .style(
                            'left',
                            `${this.$d3.event.pageX - tooltip.node().offsetWidth / 2 - 10}px`
                        )
                        .style(
                            'top',
                            `${this.$d3.event.pageY - tooltip.node().offsetHeight - 20}px`
                        );
                })
                .on('mousemove', (d) => {
                    tooltip
                        .style(
                            'left',
                            `${this.$d3.event.pageX - tooltip.node().offsetWidth / 2 - 10}px`
                        )
                        .style(
                            'top',
                            `${this.$d3.event.pageY - tooltip.node().offsetHeight - 20}px`
                        );
                })
                .on('mouseout', (d) => {
                    tooltip.transition().duration(200).style('opacity', '0');
                });

            // Transição dos eixos
            chart.select('.y').transition().duration(500).delay(delay).style('opacity', '1');

            chart.select('.x').transition().duration(500).delay(delay).style('opacity', '1');

            slice.selectAll('.bars').transition().duration(500).delay(delay).style('opacity', '1');
        },
        customFormatter(number) {
            let p = this.formatter.formatToParts(number);

            let g = p.filter((e) => {
                return e.type == 'group';
            });

            if (g.length < 1) {
                return this.formatter.format(number);
            } else {
                let sufix = '';
                let value = '';

                if (p[0].value.length == 3) {
                    value = p[0].value;
                } else {
                    value = `${p[0].value}${
                        p[0].value.length < 2 ? this.$t('scale-separator') : ''
                    }${p[2].value.substring(0, 2 - p[0].value.length)}`;
                }

                switch (g.length) {
                    case 1:
                        sufix = 'k';
                        break;
                    case 2:
                        sufix = 'M';
                        break;
                    case 3:
                        sufix = 'B';
                        break;
                    case 4:
                        sufix = 'T';
                        break;
                }

                return `${value}${sufix}`;
            }
        },
        redrawChart() {
            document.querySelector('.svg-content g').remove();
            this.drawChart();
        },
    },
};
</script>

<style lang="scss">
.legendas {
    display: flex;
    justify-content: center;
    width: 100%;
    height: 30px;
    margin-top: 15px;

    .legenda {
        display: flex;
        align-items: center;

        .color-box {
            width: 15px;
            height: 15px;
        }

        .legenda-text {
            margin-left: 5px;
            text-transform: uppercase;
            font-weight: 600;
            color: $darker-gray-color;
        }

        &:not(:first-child) {
            margin-left: 20px;
        }
    }
}

.chart-content {
    overflow-x: auto;

    .svg-content {
        height: 500px;
    }
}

.tooltip {
    position: absolute;
    color: $sovis-white;
    z-index: 50;
    transform: translate(10px, 10px);
    opacity: 0;
}

.tooltip-table {
    td {
        padding-right: 5px;
        border: none;
    }
}
</style>
